import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, AlertCircle, UserCheck, CheckCircle2, X, User } from 'lucide-react';
import { cn } from '../lib/utils';
import SuccessScreen from './ui/SuccessScreen';
import BeautifiedErrorAlert from './ui/BeautifiedErrorAlert';

import CustomerForm from './bills/CustomerForm'; // Reusing existing form
import { customerAPI } from '../services/api';
import useServerValidation from '../hooks/useServerValidation';

const CustomerModal = ({ isOpen, onClose, customerToEdit, onSuccess }) => {
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [errors, setErrors] = useState({});
    const { applyServerErrors } = useServerValidation({ setErrors });
    const [isSuccess, setIsSuccess] = useState(false);
    const [countdown, setCountdown] = useState(3);

    useEffect(() => {
        let timer;
        if (isSuccess && countdown > 0) {
            timer = setInterval(() => setCountdown(c => c - 1), 1000);
        } else if (isSuccess && countdown === 0) {
            onClose();
        }
        return () => clearInterval(timer);
    }, [isSuccess, countdown, onClose]);

    // Initialize form data
    useEffect(() => {
        if (isOpen) {
            if (customerToEdit) {
                setFormData({ ...customerToEdit }); // Copy for editing
            } else {
                // Reset for new customer
                setFormData({
                    customerName: '',
                    customerContactNumber: '',
                    customerEmail: '',
                    customerGSTNumber: '',
                    billingAddressLine: '',
                    billingCity: '',
                    billingState: 'Maharashtra',
                    billingPincode: '',
                    shippingAddressLine: '',
                    shippingCity: '',
                    shippingState: 'Maharashtra',
                    shippingPincode: ''
                });
            }
            setSubmitError(null);
            setErrors({});
        }
    }, [isOpen, customerToEdit, isSuccess]);

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSubmitError(null);
        setErrors({});

        const newErrors = {};

        // Basic Validation
        if (!formData.customerName?.trim()) newErrors.customerName = 'Customer Name is required';
        if (!formData.customerContactNumber?.trim()) newErrors.customerContactNumber = 'Contact Number is required';
        if (!formData.billingState?.trim()) newErrors.billingState = 'State is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            setSubmitError("Please fill in all required fields (Name, Contact, State).");
            setLoading(false);
            return;
        }

        try {
            if (customerToEdit) {
                // Update
                await customerAPI.updateCustomer(customerToEdit.customerId, formData);
            } else {
                // Create
                await customerAPI.createCustomer(formData);
            }
            if (onSuccess) onSuccess();
            setIsSuccess(true);
        } catch (err) {
            console.error("Failed to save customer:", err);
            const serverErrors = applyServerErrors(err);
            const msg = serverErrors._form || err.response?.data?.message || err.message || "Failed to save customer.";
            setSubmitError(msg);
        } finally {
            setLoading(false);
        }
    };

    // Safe handler for onCustomerSearch dummy implementation if needed by CustomerForm
    const handleCustomerSearch = (phone) => {
        // In standalone edit mode, we might not want to auto-fill from DB as we are likely creating/editing that DB entry.
        // However, if creating new, checking if exists might be nice. For now, leave empty or log.
        console.log("Searching customer during entry (CustomerModal):", phone);
    };

    if (!isOpen) return null;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className={cn(
                "overflow-hidden flex flex-col p-0 transition-all duration-500 rounded-[3rem] border-0 shadow-2xl ring-1 ring-white/20 dark:bg-slate-950 dark:ring-white/10 dark:text-slate-100",
                isSuccess ? "max-w-md max-h-[500px]" : "max-w-4xl max-h-[95vh]"
            )}>
              {isSuccess ? (
                <SuccessScreen 
                    message={customerToEdit ? "Customer Updated!" : "Customer Added!"}
                    subMessage="Finalizing states..."
                    countdown={countdown}
                    onAction={() => setCountdown(0)}
                />
              ) : (
                <>
                <DialogHeader className="p-6 pb-2 border-b bg-slate-50/30 dark:bg-slate-900/60 dark:border-slate-800 backdrop-blur-3xl sticky top-0 z-40">
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-2xl bg-[#FF7D00]/10 text-[#FF7D00] shadow-sm">
                            {customerToEdit ? <UserCheck className="w-6 h-6" /> : <User className="w-6 h-6" />}
                        </div>
                        <div>
                            <DialogTitle className="text-2xl font-black tracking-tight text-slate-800 dark:text-white">
                                {customerToEdit ? 'Update Client Record' : 'Registry New Customer'}
                            </DialogTitle>
                            <DialogDescription className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-0.5">
                                Manage contact details, GST profiles and address information.
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-6 py-6 scroll-smooth">
                    {submitError && (
                        <div className="mb-6 animate-in slide-in-from-top-4 duration-300">
                            <BeautifiedErrorAlert 
                                error={submitError} 
                                onDismiss={() => {
                                    setSubmitError(null);
                                }} 
                            />
                        </div>
                    )}


                <div className="py-4">
                    {/* Reusing the CustomerForm component */}
                    <CustomerForm
                        customer={formData}
                        onChange={handleChange}
                        onCustomerSearch={handleCustomerSearch}
                        errors={errors}
                    />
                </div>
                </div>

                <DialogFooter className="p-6 pt-4 border-t bg-slate-50/50 dark:bg-slate-900/60 dark:border-slate-800 sticky bottom-0 z-30 sm:justify-end items-center gap-4 shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.05)]">
                    <div className="flex gap-4 w-full sm:w-auto">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={loading}
                            className="flex-1 sm:flex-none h-12 px-8 rounded-xl border-slate-200 dark:border-slate-800 font-bold text-slate-500 dark:text-slate-400"
                        >
                            Discard
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSubmit}
                            disabled={loading || !formData}
                            className="flex-1 sm:flex-none h-12 px-12 rounded-xl bg-[#FF7D00] hover:brightness-110 active:scale-95 text-white font-black uppercase tracking-widest shadow-xl shadow-orange-500/10 transition-all duration-200"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                "Save Customer"
                            )}
                        </Button>
                    </div>
                </DialogFooter>
                </>
              )}
            </DialogContent>
        </Dialog>
    );
};

export default CustomerModal;
